/**
 * Shared URL/hostname helpers.
 *
 * Consolidates URL → hostname extraction and domain manipulation logic that was
 * previously duplicated across livenessScripts.js (asset URL parsing) and
 * SuggestedPolicyModal.vue (policy domain matching).
 */

// Sentinel base used to parse relative URLs without throwing. After parsing,
// callers should treat a hostname equal to PARSER_BASE_HOST as "no real host"
// (i.e. the input was relative or unparseable as absolute).
const PARSER_BASE_URL = 'https://local-parser-base.invalid';
export const PARSER_BASE_HOST = 'local-parser-base.invalid';

/**
 * Normalize a hostname for comparison: trim, lowercase, strip leading "www.",
 * and strip trailing dots (FQDN form).
 * @param {string} hostname
 * @returns {string}
 */
export function normalizeHostname(hostname) {
  return String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.+$/, '');
}

/**
 * Parse a possibly-relative URL using a sentinel base so that relative paths
 * don't throw. Returns null if parsing fails entirely.
 * @param {string} value
 * @returns {URL|null}
 */
export function parseUrlWithBase(value) {
  try {
    return new URL(value, PARSER_BASE_URL);
  } catch (_) {
    return null;
  }
}

/**
 * Best-effort extraction of a normalized hostname from a URL string, bare
 * domain, or domain-with-path. Tries:
 *   1. `new URL(value)` — works for fully-qualified absolute URLs.
 *   2. `new URL('https://' + value)` — works for bare domains and domain+path.
 *   3. Regex fallback — handles malformed inputs like missing scheme separators.
 *
 * Returns null if no hostname could be extracted.
 * @param {string} value
 * @returns {string|null}
 */
export function extractHostname(value) {
  if (!value) return null;
  const str = String(value);

  try {
    return normalizeHostname(new URL(str).hostname);
  } catch (_) { /* fall through */ }

  try {
    return normalizeHostname(new URL('https://' + str).hostname);
  } catch (_) { /* fall through */ }

  const match = str.match(/^(?:https?:\/\/)?([^/\s:]+)/i);
  return match ? normalizeHostname(match[1]) : null;
}

/**
 * Get the registrable two-label root domain (e.g. "sub.example.co.uk" → "co.uk").
 * Note: this is a heuristic, not a Public Suffix List lookup.
 * @param {string} hostname
 * @returns {string}
 */
export function getRootDomain(hostname) {
  const labels = normalizeHostname(hostname).split('.').filter(Boolean);
  if (labels.length <= 2) return labels.join('.');
  return labels.slice(-2).join('.');
}

/**
 * Build the list of progressively-shorter suffixes of a domain.
 * e.g. "a.b.example.com" → ["a.b.example.com", "b.example.com", "example.com", "com"]
 * @param {string} domain
 * @returns {string[]}
 */
export function getDomainSuffixes(domain) {
  const labels = normalizeHostname(domain).split('.').filter(Boolean);
  if (labels.length === 0) return [];
  return labels.map((_, index) => labels.slice(index).join('.'));
}
